import { type Calculation, calculateValueFromPoints } from "@suse-o11y/calculate-value-from-points";
import { isSinglePointCalculation } from "@suse-o11y/calculate-value-from-points/single-point";
import type { ChartThresholds } from "@suse-o11y/chart-thresholds";
import { findChartThresholdColor } from "@suse-o11y/chart-thresholds";
import { cx } from "@suse-o11y/class-variance";
import type { DataPoint } from "@suse-o11y/data-point";
import type { Interval } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { formatValue } from "@suse-o11y/format-value";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { max } from "@suse-o11y/max";
import { min } from "@suse-o11y/min";
import { useMemo } from "@suse-o11y/use-memo";
import React, { useDeferredValue } from "react";
import { useMeasure } from "react-use";

import { ChartSeriesLabel } from "~/components/chart-series-label";
import { StatArea } from "~/components/stat-area";

import styles from "./_stat-chart.module.css";
import { Text } from "./_text";
import { autoValueFontSize } from "./utils";

const MINIMAL_AREA_HEIGHT_PX = 1;
const VALUE_AREA_OFFSET_PX = 20;

interface BaseStatChartProps extends React.HTMLAttributes<HTMLDivElement> {
	calculation: Calculation | Time;
	decimalPlaces?: number;
	/**
	 * Sets the font size of the value.
	 * If not defined, the font size depends on the chart size.
	 */
	fontSize?: number;
	/**
	 * @default {@link formatValue}
	 */
	formatValue?: typeof formatValue;
	interval: Interval;
	name?: string;
	points: ReadonlyArray<DataPoint>;
	thresholds?: ChartThresholds;
	unit?: string;
	/**
	 * Render the area chart behind the value
	 *
	 * @default true
	 */
	withArea?: boolean;
}

interface StatChartPropsWithWidthHeight extends BaseStatChartProps {
	width: number;
	height: number;
}

type StatChartProps = StatChartPropsWithWidthHeight | BaseStatChartProps;

export const StatChart: React.FC<StatChartProps> = (props) => {
	if ("width" in props && "height" in props) {
		return <ExplicitSizedStatChart {...props} />;
	}

	return <AutoSizedStatChart {...props} />;
};

const ExplicitSizedStatChart = React.memo<StatChartPropsWithWidthHeight>(({ width, height, ...props }) => {
	const outerWidth = useDeferredValue(max(0, width));
	const outerHeight = useDeferredValue(max(0, height));

	return <BaseStatChart height={outerHeight} width={outerWidth} {...props} />;
}, isDeepEqual);

const AutoSizedStatChart = React.memo<BaseStatChartProps>((props) => {
	const [ref, { height, width }] = useMeasure<HTMLDivElement>();

	return <BaseStatChart height={height} ref={ref} width={width} {...props} />;
});

const BaseStatChart = React.forwardRef<HTMLDivElement, StatChartPropsWithWidthHeight>(
	(
		{
			className,
			fontSize,
			withArea = true,
			unit,
			name,
			interval,
			calculation,
			points,
			thresholds,
			decimalPlaces,
			width,
			height,
			formatValue: formatValueProp = formatValue,
			...props
		},
		ref,
	) => {
		const value = useMemo(() => calculateValueFromPoints(calculation, points), [points, calculation]);

		const formatted = useMemo(
			() => formatValueProp(value, { unit, precision: decimalPlaces }),
			[value, unit, decimalPlaces, formatValueProp],
		);

		const color = useMemo(
			() => (thresholds == null ? undefined : findChartThresholdColor(value, thresholds)),
			[thresholds, value],
			[isDeepEqual],
		);

		const valueFontSize = fontSize ?? autoValueFontSize(width, height, formatted.length);
		const nameFontSize = (min(width, height) / 100) * 8;

		const areaHeight = height - valueFontSize - nameFontSize - VALUE_AREA_OFFSET_PX;
		const isAreaVisible = withArea && points.length > 1 && areaHeight >= MINIMAL_AREA_HEIGHT_PX;

		return (
			<div className={cx(styles.statChart, className)} ref={ref} {...props}>
				{isAreaVisible && (
					<StatArea
						appearance="thin"
						calculation={isSinglePointCalculation(calculation) ? calculation : undefined}
						className="self-end"
						height={areaHeight}
						interval={interval}
						lineColor={color}
						points={points}
						width={width}
					/>
				)}

				<div className="flex min-w-0 flex-col items-center justify-center text-center">
					{name != null && <ChartSeriesLabel fontSize={nameFontSize}>{name}</ChartSeriesLabel>}

					{valueFontSize > 0 && (
						<Text className="pt-3 font-semibold" color={color} fontSize={valueFontSize}>
							{formatted}
						</Text>
					)}
				</div>
			</div>
		);
	},
);
